import {useCallback, useEffect, useMemo, useRef} from 'react'
import {
  useSelectedSectorIds,
  useUIActions,
  useLinearWindowOverride,
} from '../store'
import {useGetReferentialTracks} from './referential.hooks'
import {useListSectorsByNetwork} from './sector.hooks'
import {useTrackEvent} from './amplitude.hooks'
import {useNetworkDefaultPkRange} from './network.hooks'
import {
  TPkEnvelope,
  TWindowSource,
  effectiveSelectedSectorIds,
  getSelectedSectorsEnvelope,
  resolveBaselineEnvelope,
  resolveWindowBounds,
} from '../utils/sectorWindow.functions'
import {DEFAULT_GRANULARITY} from '../utils/blockView.functions'

/** Which linear view an instance serves — reported on `LinearViewWindowChanged`. */
export type TLinearView = 'work' | 'network' | 'settings'

export type TResolvedLinearWindow = {
  pkStart: number | undefined
  pkEnd: number | undefined
  granularity: number
  /** Both bounds present and finite — safe to fetch the viewport. */
  boundsAreValid: boolean
  /** The selected sectors' PK hull, or null — kept for the pick analytics. */
  sectorEnvelope: TPkEnvelope | null
  /** Which source currently owns the window — manual PK, sector, or extent. */
  source: TWindowSource
  /** The selected sector ids when `source === 'sector'`, otherwise empty. */
  sectorIds: string[]
}

export type TNetworkWindowState = Omit<TResolvedLinearWindow, 'sectorEnvelope'>

export type TNetworkWindowOptions = {
  /** See {@link TLinearView}. */
  view: TLinearView
  /**
   * Whether this instance currently owns the visible window. The sector effect
   * reacts to a network-wide store, but the data wrapper mounts a consumer on
   * EVERY tab — so an inactive instance must neither reset the manual window nor
   * report the pick, or a sector chosen to filter (say) the activities list
   * would silently wipe the work view's typed PK and mis-fire analytics.
   * Defaults to true.
   */
  active?: boolean
}

const span = (bounds: TPkEnvelope): number =>
  Math.round(Math.abs(bounds.kmEnd - bounds.kmStart))

// Stable empty selection: a fresh [] every render would break referential
// equality for every memo keyed on `sectorIds` downstream.
const NO_SECTORS: string[] = []

/**
 * Resolves the effective PK window of the linear view from the shared store —
 * the manual override (typed PK bounds + ruler step) and the sector selection —
 * falling back to the network's baseline (configured default window, else
 * referential extent). Precedence: manual → sector → baseline (see
 * `resolveWindowBounds`).
 *
 * Pure derivation, NO effects: usable both by the consumer ({@link
 * useNetworkWindow}) and by the header controls that must display the resolved
 * value. State lives in the store so those controls can render in the global
 * header, above the data wrapper that consumes the window.
 */
export const useResolvedLinearWindow = (
  networkId: string,
): TResolvedLinearWindow => {
  const override = useLinearWindowOverride()

  const {data: labels} = useGetReferentialTracks(networkId)
  const extent = useMemo<TPkEnvelope | null>(() => {
    if (!labels || labels.length === 0) return null
    const bounds = labels.flatMap(label =>
      label.segments.flatMap(segment => [segment.kmStart, segment.kmEnd]),
    )
    if (bounds.length === 0) return null
    return {kmStart: Math.min(...bounds), kmEnd: Math.max(...bounds)}
  }, [labels])

  const selectedSectorIds = useSelectedSectorIds()
  const {data: sectors} = useListSectorsByNetwork(networkId)
  // Only the ids that still exist on THIS network drive the window. A stale
  // selection (another network's sectors, or a since-reset demo network) would
  // otherwise force sector mode with an id that fetches an empty viewport,
  // hanging the linear view on a null axis. See `effectiveSelectedSectorIds`.
  const activeSectorIds = useMemo(
    () => effectiveSelectedSectorIds(selectedSectorIds, sectors),
    [selectedSectorIds, sectors],
  )
  const sectorEnvelope = useMemo(
    () => getSelectedSectorsEnvelope(sectors ?? [], activeSectorIds),
    [sectors, activeSectorIds],
  )

  // The baseline the resolution falls through to: the network-configured
  // default window when one exists (clamped to the real extent), otherwise
  // the extent itself.
  const networkDefault = useNetworkDefaultPkRange(networkId)
  const baseline = useMemo(
    () => resolveBaselineEnvelope(networkDefault, extent),
    [networkDefault, extent],
  )

  const {pkStart, pkEnd, granularity} = resolveWindowBounds(
    override,
    sectorEnvelope,
    baseline,
    DEFAULT_GRANULARITY,
  )
  const boundsAreValid =
    pkStart !== undefined &&
    pkEnd !== undefined &&
    Number.isFinite(pkStart) &&
    Number.isFinite(pkEnd)

  const source: TWindowSource =
    override.pkStart !== undefined || override.pkEnd !== undefined
      ? 'manualPk'
      : activeSectorIds.length > 0
        ? 'sector'
        : 'extent'

  return {
    pkStart,
    pkEnd,
    granularity,
    boundsAreValid,
    sectorEnvelope,
    source,
    sectorIds: source === 'sector' ? activeSectorIds : NO_SECTORS,
  }
}

/**
 * The CONSUMER of the linear-view window: one instance per view (data wrapper for
 * work, NetworkView for network). Resolves the window and owns the "a sector pick
 * resets the window afresh" rule + its analytics. The CONTROLS use
 * {@link useLinearWindowControls} instead.
 */
export const useNetworkWindow = (
  networkId: string,
  {view, active = true}: TNetworkWindowOptions,
): TNetworkWindowState => {
  const trackEvent = useTrackEvent()
  const {clearLinearWindowBounds} = useUIActions()
  const selectedSectorIds = useSelectedSectorIds()
  const {
    pkStart,
    pkEnd,
    granularity,
    boundsAreValid,
    sectorEnvelope,
    source,
    sectorIds,
  } = useResolvedLinearWindow(networkId)

  // Refs so the sector effect reacts without re-firing when the pathname/role
  // that rebuilds `trackEvent` shifts under it.
  const envelopeRef = useRef(sectorEnvelope)
  envelopeRef.current = sectorEnvelope
  const trackEventRef = useRef(trackEvent)
  trackEventRef.current = trackEvent
  const activeRef = useRef(active)
  activeRef.current = active
  const viewRef = useRef(view)
  viewRef.current = view
  const clearBoundsRef = useRef(clearLinearWindowBounds)
  clearBoundsRef.current = clearLinearWindowBounds

  // A sector pick sets the window afresh: drop any manual PK tweak (keep
  // granularity), and report the resolved span. The reference compare skips the
  // mount pass and any StrictMode replay — only a genuinely new selection (a new
  // array reference from the store) counts as a fresh pick.
  //
  // Only a NON-EMPTY selection resets the manual window: when the selection is
  // cleared — including when a manual PK edit clears it for mutual exclusion —
  // the just-typed PK must stand, not be wiped here.
  const prevSelectedRef = useRef(selectedSectorIds)
  useEffect(() => {
    if (prevSelectedRef.current === selectedSectorIds) return
    prevSelectedRef.current = selectedSectorIds
    if (selectedSectorIds.length === 0) return
    // Inactive instance (e.g. the wrapper's consumer on a non-work tab): the pick
    // belongs to another view — do not reset this window or report it.
    if (!activeRef.current) return

    clearBoundsRef.current()
    const envelope = envelopeRef.current
    if (envelope) {
      trackEventRef.current('LinearViewWindowChanged', {
        method: 'sector',
        view: viewRef.current,
        spanKm: span(envelope),
      })
    }
  }, [selectedSectorIds])

  return {pkStart, pkEnd, granularity, boundsAreValid, source, sectorIds}
}

export type TLinearWindowControls = {
  /** The resolved window to display in the inputs (override → sector → extent). */
  resolved: TResolvedLinearWindow
  /** Merge typed PK bounds; supersedes any sector selection (mutual exclusion). */
  onBoundsChange: (next: {pkStart?: number; pkEnd?: number}) => void
  /** Set the ruler step — display density, orthogonal to the window source. */
  onGranularityChange: (granularity: number) => void
  /**
   * Report the ruler step once the user settles on it (slider release), so a
   * drag across marks fires one event, not one per mark. Answers "do users
   * re-zoom the linear view, and to what density?" — i.e. is the default good.
   */
  onGranularityCommit: (granularity: number) => void
}

/**
 * The write side of the linear-view window, for the header controls. Exposes the
 * resolved window (for display) and the change handlers. Kept apart from
 * {@link useNetworkWindow} so the controls can live in the global header without
 * running the consumer's effects (which would double-fire across instances).
 */
export const useLinearWindowControls = (
  networkId: string,
  view: TLinearView,
): TLinearWindowControls => {
  const trackEvent = useTrackEvent()
  const resolved = useResolvedLinearWindow(networkId)
  const override = useLinearWindowOverride()
  const selectedSectorIds = useSelectedSectorIds()
  const {
    setLinearWindowBounds,
    setLinearWindowGranularity,
    setSelectedSectorIds,
  } = useUIActions()

  const onBoundsChange = useCallback(
    (next: {pkStart?: number; pkEnd?: number}) => {
      setLinearWindowBounds(next)

      // Mutual exclusion: a manual PK window and a sector selection must never
      // filter at once. Typing a PK supersedes the sector.
      if (selectedSectorIds.length > 0) setSelectedSectorIds([])

      // Report once the window is a real, complete range.
      const merged = {...override, ...next}
      if (
        merged.pkStart !== undefined &&
        merged.pkEnd !== undefined &&
        Number.isFinite(merged.pkStart) &&
        Number.isFinite(merged.pkEnd)
      ) {
        trackEvent('LinearViewWindowChanged', {
          method: 'manualPk',
          view,
          spanKm: Math.round(Math.abs(merged.pkEnd - merged.pkStart)),
        })
      }
    },
    [
      override,
      selectedSectorIds,
      setLinearWindowBounds,
      setSelectedSectorIds,
      trackEvent,
      view,
    ],
  )

  const onGranularityCommit = useCallback(
    (granularity: number) => {
      trackEvent('LinearViewWindowChanged', {
        method: 'granularity',
        view,
        granularity,
      })
    },
    [trackEvent, view],
  )

  return {
    resolved,
    onBoundsChange,
    onGranularityChange: setLinearWindowGranularity,
    onGranularityCommit,
  }
}
